import { faCircleCheck, faEnvelope, faPlus, faRotateRight, faTrash, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Divider,
  Group,
  Stack,
  Text,
  Tooltip
} from "@mantine/core";
import { DateTime } from "luxon";
import * as React from "react";
import CommunicationApi, { BlockedReason, IVerifiedEmail } from "../../data/api/communicationApi";
import { toDateTime } from "../../util/date+helper";
import { CenteredLoader } from "../common/loader";
import { PersonalNotificationSettings } from "./PersonalNotificationSettings";
import { IPersonalSettingsProps } from "./PersonalSettings";

interface IEmailRowState {
  resendLoading?: boolean;
  archiveLoading?: boolean;
  cooldownEnds?: DateTime | null;
  message?: string | null;
}

export const PersonalEmailSettings: React.FunctionComponent<IPersonalSettingsProps> = (props) => {
  const [emails, setEmails] = React.useState<IVerifiedEmail[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [rowState, setRowState] = React.useState<{ [id: string]: IEmailRowState }>({});
  const [adding, setAdding] = React.useState<boolean>(false);

  const fetchEmails = React.useCallback(() => {
    setError(null);
    CommunicationApi.getMyEmails()
      .then((_emails) => setEmails(_emails))
      .catch((err) => setError(err.message));
  }, []);

  React.useEffect(() => {
    fetchEmails();
  }, [fetchEmails, props.person?._id]);

  const addEmail = () => {
    const value = prompt("New email address");
    if (!value) return;
    setAdding(true);
    CommunicationApi.addEmail(value)
      .then(() => fetchEmails())
      .catch((err) => setError(err.message))
      .finally(() => setAdding(false));
  };

  const resendVerification = (email: IVerifiedEmail) => {
    setRowState((r) => ({
      ...r,
      [email._id]: { ...r[email._id], resendLoading: true, message: null }
    }));
    CommunicationApi.addEmail(email.email)
      .then(() => {
        setRowState((r) => ({
          ...r,
          [email._id]: {
            ...r[email._id],
            resendLoading: false,
            cooldownEnds: DateTime.now().plus({ seconds: 30 }),
            message: "Verification email sent"
          }
        }));
      })
      .catch((er) => {
        setRowState((r) => ({
          ...r,
          [email._id]: { ...r[email._id], resendLoading: false, message: er.message }
        }));
      });
  };

  const archiveEmail = (email: IVerifiedEmail) => {
    setRowState((r) => ({ ...r, [email._id]: { ...r[email._id], archiveLoading: true } }));
    CommunicationApi.archiveEmail(email._id)
      .then(() => fetchEmails())
      .catch((er) => {
        setRowState((r) => ({
          ...r,
          [email._id]: { ...r[email._id], archiveLoading: false, message: er.message }
        }));
      });
  };

  const canResend = (email: IVerifiedEmail): boolean => {
    if (email.archived || email.dateVerified || email.blockedDate) return false;
    const rs = rowState[email._id];
    if (rs?.cooldownEnds && rs.cooldownEnds > DateTime.now()) return false;
    // original logic required > 1 minute from creation for first resend
    const created = toDateTime(email.dateCreated);
    if (created && created.plus({ minute: 1 }) > DateTime.now()) return false;
    return true;
  };

  const statusBadge = (email: IVerifiedEmail) => {
    if (email.blockedDate)
      return <Badge color="red" variant="light">Blocked</Badge>;
    if (email.archived)
      return <Badge color="gray" variant="light">Archived</Badge>;
    if (email.dateVerified)
      return <Badge color="green" variant="light" leftSection={<FontAwesomeIcon icon={faCircleCheck} />}>Verified</Badge>;
    return <Badge color="yellow" variant="light">Pending</Badge>;
  };

  const activeEmails = emails?.filter(e => !e.archived) || [];
  const archivedEmails = emails?.filter(e => e.archived) || [];

  return (
    <Stack gap="lg">
      {error && (
        <Alert color="red" variant="light" radius="md">{error}</Alert>
      )}

      {/* Notification Settings */}
      <Card withBorder padding="lg" radius="md" shadow="sm">
        <Group justify="space-between" mb="sm">
          <Group gap="sm">
            <FontAwesomeIcon icon={faEnvelope} style={{ color: 'var(--color-button-primary)' }} />
            <div>
              <h4 style={{ margin: 0 }}>Notification</h4>
              <Text size="sm" c="dimmed">Control how StadiumSupervisor emails you</Text>
            </div>
          </Group>
        </Group>
        <Divider my="sm" label="Notification settings" labelPosition="left" />
        <PersonalNotificationSettings {...props} />
      </Card>

      {/* Emails Table */}
      <Card withBorder padding="lg" radius="md" shadow="sm">
        <Group justify="space-between" mb="sm">
          <Group gap="sm">
            <FontAwesomeIcon icon={faEnvelope} style={{ color: 'var(--color-button-primary)' }} />
            <div>
              <h4 style={{ margin: 0 }}>Emails</h4>
              <Text size="sm" c="dimmed">Manage & verify the email addresses we can contact you on.</Text>
            </div>
          </Group>
          <Button size="sm" leftSection={<FontAwesomeIcon icon={faPlus} />} loading={adding} onClick={addEmail}>Add email</Button>
        </Group>
        <Text size="xs" c="dimmed" mb="sm"><em>Adding and verifying an email address will invalidate older unverified addresses.</em></Text>

        {emails === null && <CenteredLoader />}
        {emails && emails.length === 0 && (
          <Alert color="blue" variant="light">You have no email addresses yet. Add one to get started.</Alert>
        )}

        {activeEmails.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: 'left' }}>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-table-border)' }}>Email</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-table-border)' }}>Status</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-table-border)' }}>Created</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-table-border)' }}>Verified</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-table-border)' }}>Last Updated</th>
                  <th style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-table-border)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeEmails.map(email => {
                  const rs = rowState[email._id];
                  const cooldownRemaining = rs?.cooldownEnds && rs.cooldownEnds > DateTime.now()
                    ? Math.ceil(rs.cooldownEnds.diff(DateTime.now(), 'seconds').seconds)
                    : null;
                  return (
                    <tr key={email._id} style={{ borderBottom: '1px solid var(--color-table-border)' }}>
                      <td style={{ padding: '6px 8px', fontFamily: 'var(--mantine-font-monospace)' }}>{email.email}</td>
                      <td style={{ padding: '6px 8px' }}>{statusBadge(email)}</td>
                      <td style={{ padding: '6px 8px' }}>{toDateTime(email.dateCreated)?.toLocaleString(DateTime.DATETIME_SHORT)}</td>
                      <td style={{ padding: '6px 8px' }}>{email.dateVerified ? toDateTime(email.dateVerified)?.toLocaleString(DateTime.DATETIME_SHORT) : '—'}</td>
                      <td style={{ padding: '6px 8px' }}>{email.blockedDate ? toDateTime(email.blockedDate)?.toLocaleString(DateTime.DATETIME_SHORT) : '—'}</td>
                      <td style={{ padding: '6px 8px' }}>
                        <Group gap={4} wrap="nowrap">
                          {!email.dateVerified && !email.archived && !email.blockedDate && (
                            <Tooltip label={cooldownRemaining ? `Wait ${cooldownRemaining}s` : 'Resend verification'} withArrow>
                              <ActionIcon size="sm" variant="light" color="blue" disabled={!canResend(email)} loading={rowState[email._id]?.resendLoading} onClick={() => canResend(email) && resendVerification(email)}>
                                <FontAwesomeIcon icon={faRotateRight} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {!email.archived && (
                            <Tooltip label="Archive" withArrow>
                              <ActionIcon size="sm" variant="light" color="red" loading={rowState[email._id]?.archiveLoading} onClick={() => archiveEmail(email)}>
                                <FontAwesomeIcon icon={faTrash} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                          {email.blockedReason && (
                            <Tooltip label={BlockedReason.toString(email.blockedReason)} withArrow>
                              <ActionIcon size="sm" variant="subtle" color="red">
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                              </ActionIcon>
                            </Tooltip>
                          )}
                        </Group>
                        {rs?.message && (
                          <Text size="xs" c="dimmed" mt={4}>{rs.message}</Text>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {archivedEmails.length > 0 && (
          <Stack gap="xs" mt="lg">
            <Divider label="Archived" labelPosition="left" />
            <Code block style={{ fontSize: 12 }}>
              {archivedEmails.map(e => e.email).join("\n")}
            </Code>
          </Stack>
        )}
      </Card>
    </Stack>
  );
};
